import { apiClient } from './apiClient';
import { BatchResultStatus, BatchStatus } from '@/services/enums';

export interface WatchlistItem {
  id: number;
  symbol: string;
  instType: string;
  createdAt: string;
  currentPrice?: string; // 当前价格
  change5m?: string;
  change1h?: string;
  change1d?: string;
  change7d?: string;
  price5mAgo?: string;
  price1hAgo?: string;
  price1dAgo?: string;
  price7dAgo?: string;
}

export interface AddToWatchlistRequest {
  symbol: string;
  instType: string;
}

export interface CheckWatchlistResponse {
  isWatched: boolean;
  watchlistId?: number;
}

export interface WatchlistResponse {
  success: boolean; // Keep for compatibility if needed, though ApiResponse handles it
  data: WatchlistItem[];
}

export interface WatchlistBatchResultItem {
  symbol: string;
  strategyCode: string;
  strategyName: string;
  status: BatchResultStatus;
  totalReturnPct?: number;
  winRate?: number;
  totalTrades?: number;
  error?: string;
  finishedAt?: string;
}

export interface WatchlistBatchBacktestStatus {
  taskId: string;
  status: BatchStatus;
  message?: string;
  totalTasks: number;
  completedTasks: number;
  currentTask?: {
    symbol: string;
    strategyCode: string;
    strategyName: string;
  };
  results: WatchlistBatchResultItem[];
  startedAt?: string;
  completedAt?: string;
  error?: string;
}

export class WatchlistService {
  /**
   * 添加币种到关注列表
   */
  async addToWatchlist(symbol: string, instType: string): Promise<WatchlistItem> {
    const response = await apiClient.post<WatchlistItem>('/watchlist/add', {
      symbol,
      instType,
    });

    if (!response.success) {
      throw new Error(response.message || '添加关注失败');
    }

    // 返回接口返回的数据
    return response.data;
  }

  /**
   * 从关注列表中移除币种
   */
  async removeFromWatchlist(symbol: string, instType: string): Promise<any> {
    const response = await apiClient.delete<any>('/watchlist/remove', {
      data: {
        symbol,
        instType,
      },
    });

    if (!response.success) {
      throw new Error(response.message || '取消关注失败');
    }

    return response.data;
  }

  /**
   * 获取用户关注列表
   */
  async getWatchlist(): Promise<WatchlistItem[]> {
    const response = await apiClient.get<WatchlistItem[]>('/watchlist');

    if (!response.success) {
      throw new Error(response.message || '获取关注列表失败');
    }

    return response.data || [];
  }

  /**
   * 检查是否已关注某币种
   */
  async checkIsWatched(symbol: string, instType: string): Promise<boolean> {
    try {
      const response = await apiClient.get<CheckWatchlistResponse>('/watchlist/check', {
        params: {
          symbol,
          instType,
        },
      });

      if (!response.success) {
        return false;
      }

      return response.data?.isWatched || false;
    } catch (error) {
      console.error('检查关注状态失败:', error);
      return false;
    }
  }

  /**
   * 获取关注列表详情
   */
  async getWatchlistDetail(): Promise<WatchlistItem[]> {
    const response = await apiClient.get<WatchlistItem[]>('/watchlist/detail');

    if (!response.success) {
      throw new Error(response.message || '获取关注列表详情失败');
    }

    return response.data || [];
  }

  async startBatchBacktest(): Promise<WatchlistBatchBacktestStatus> {
    const response = await apiClient.post<WatchlistBatchBacktestStatus>('/watchlist/batch-backtest');
    if (!response.success) {
      throw new Error(response.message || '批量回测启动失败');
    }
    return response.data;
  }

  async getBatchBacktestStatus(): Promise<WatchlistBatchBacktestStatus | null> {
    const response = await apiClient.get<WatchlistBatchBacktestStatus | null>('/watchlist/batch-backtest/status');
    if (!response.success) {
      throw new Error(response.message || '获取批量回测状态失败');
    }
    return response.data || null;
  }
}

// 创建全局实例
export const watchlistService = new WatchlistService();
